import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    BookOpen, Clock, Users, Tag, FileText, Download,
    CheckCircle, Video, ChevronDown, ChevronUp,
    AlertCircle, Target, Play,
    Layers, BarChart2, Info, X, ArrowLeft,
} from 'lucide-react';
import trainingService from '../../../api/services/trainingService';
import toast from 'react-hot-toast';

// ─── Brand colour tokens (matches tailwind.config.js) ───────────────────────
const C = {
    // Primary – green
    green: '#22c55e',
    greenDark: '#15803d',
    greenMid: '#16a34a',
    green50: '#f0fdf4',
    green100: '#dcfce7',
    green700: '#15803d',

    // Secondary – blue
    blue: '#3b82f6',
    blueDark: '#1d4ed8',
    blueDarker: '#1e40af',
    blue50: '#eff6ff',
    blue100: '#dbeafe',
    blue700: '#1d4ed8',

    // Neutrals
    gray50: '#f9fafb',
    gray100: '#f3f4f6',
    gray200: '#e5e7eb',
    gray300: '#d1d5db',
    gray400: '#9ca3af',
    gray500: '#6b7280',
    gray600: '#4b5563',
    gray700: '#374151',
    gray800: '#1f2937',
    gray900: '#111827',

    // Semantics
    yellow: '#f59e0b',
    yellowLight: '#fef9c3',
    yellowDark: '#854d0e',
    red: '#ef4444',
    redLight: '#fee2e2',
    redDark: '#b91c1c',
    purple: '#7c3aed',
    purpleLight: '#f3e8ff',
    orange: '#c2410c',
    orangeLight: '#ffedd5',

    // Hero header bg
    heroBg: 'linear-gradient(135deg, #064e3b 0%, #065f46 40%, #15803d 100%)',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const capitalizeFirst = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const capitalizeValue = (value) => {
    if (!value) return '';
    const special = {
        soft_skills: 'Soft Skills', all_employees: 'All Employees',
        super_admin: 'Super Admin', hr_manager: 'HR Manager',
        hr_executive: 'HR Executive', sales_manager: 'Sales Manager',
        sales_executive: 'Sales Executive', business_relationship_manager: 'Business Relationship Manager',
        business_development_executive: 'Business Development Executive', dispatch_manager: 'Dispatch Manager',
        support_manager: 'Support Manager',
    };
    return special[value] || value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
};

const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return '0m';
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 && hours === 0) parts.push(`${secs}s`);
    return parts.length > 0 ? parts.join(' ') : '0m';
};

const normalizeObjectives = (value) => {
    if (!value) return [];

    if (Array.isArray(value)) {
        return value
            .map((item) => {
                if (typeof item === 'string') return item.trim();
                if (item && typeof item === 'object') {
                    return (item.title || item.objective || item.text || '').trim();
                }
                return '';
            })
            .filter(Boolean);
    }

    if (typeof value === 'string') {
        return value
            .split(/\r?\n|,|;/)
            .map((item) => item.trim())
            .filter(Boolean);
    }

    return [];
};

const getFilenameFromUrl = (url) => {
    try {
        const parsed = new URL(url);
        const lastPart = decodeURIComponent(parsed.pathname.split('/').pop() || '').trim();
        return /\.[a-zA-Z0-9]+$/.test(lastPart) ? lastPart : '';
    } catch (error) {
        return '';
    }
};

const ensureFilenameHasExtension = (filename, mimeType) => {
    const clean = String(filename || 'file').trim();
    if (/\.[a-zA-Z0-9]+$/.test(clean)) return clean;

    const type = String(mimeType || '').toLowerCase();
    if (type === 'video/mp4') return `${clean}.mp4`;
    if (type === 'video/webm') return `${clean}.webm`;
    if (type === 'video/ogg') return `${clean}.ogg`;
    if (type === 'application/pdf') return `${clean}.pdf`;
    if (type === 'application/msword') return `${clean}.doc`;
    if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return `${clean}.docx`;
    if (type === 'application/vnd.ms-powerpoint') return `${clean}.ppt`;
    if (type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return `${clean}.pptx`;
    return clean;
};

const handleDownloadUploadedFile = async (fileUrl, fileName = 'file') => {
    if (!fileUrl) {
        toast.error('No file URL available');
        return;
    }

    const baseName = fileName || getFilenameFromUrl(fileUrl) || 'file';

    try {
        const response = await authFetch(fileUrl);
        if (!response.ok) throw new Error('Failed to fetch file');
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const finalName = ensureFilenameHasExtension(baseName, blob.type);

        const link = document.body.appendChild(document.createElement('a'));
        link.href = blobUrl;
        link.download = finalName;
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
        // Fallback for CORS-restricted URLs or fetch failures
        const link = document.body.appendChild(document.createElement('a'));
        link.href = fileUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
        document.body.removeChild(link);
    }
};

const DIFFICULTY_CONFIG = {
    beginner: { label: 'Beginner', color: C.green, bg: C.green100, icon: '🌱' },
    intermediate: { label: 'Intermediate', color: C.yellow, bg: '#fef3c7', icon: '⚡' },
    advanced: { label: 'Advanced', color: C.red, bg: C.redLight, icon: '🔥' },
};

const STATUS_CONFIG = {
    published: { label: 'Published', color: C.green, bg: C.green100 },
    draft: { label: 'Draft', color: C.yellow, bg: '#fef3c7' },
    archived: { label: 'Archived', color: C.gray500, bg: C.gray100 },
};

const LESSON_TYPE_CONFIG = {
    video: { label: 'Video', bg: C.blue100, color: C.blue700, icon: Video },
    youtube: { label: 'YouTube', bg: C.redLight, color: C.redDark, icon: Video },
    file: { label: 'Document', bg: C.purpleLight, color: C.purple, icon: FileText },
    document: { label: 'Document', bg: C.purpleLight, color: C.purple, icon: FileText },
    quiz: { label: 'Quiz', bg: C.orangeLight, color: C.orange, icon: CheckCircle },
    assessment: { label: 'Assessment', bg: C.orangeLight, color: C.orange, icon: CheckCircle },
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const InfoBadge = ({ icon: Icon, label, value, color = C.green }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: 12, padding: '12px 16px',
    }}>
        <div style={{
            width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.15)', flexShrink: 0,
        }}>
            <Icon size={16} color="#fff" />
        </div>
        <div>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</p>
            <p style={{ fontSize: 13, color: '#fff', fontWeight: 600, margin: 0 }}>{value || '—'}</p>
        </div>
    </div>
);

const SectionCard = ({ title, icon: Icon, children, accent = C.green }) => (
    <div style={{
        background: '#fff', borderRadius: 12, border: `1px solid ${C.gray200}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)', overflow: 'hidden', marginBottom: 20,
    }}>
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '16px 22px', borderBottom: `1px solid ${C.gray100}`,
            background: C.gray50,
        }}>
            <div style={{
                width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${accent}18`,
            }}>
                <Icon size={16} color={accent} />
            </div>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: C.gray800, margin: 0 }}>{title}</h3>
        </div>
        <div style={{ padding: '18px 22px' }}>{children}</div>
    </div>
);

const LessonTypeIcon = ({ type }) => {
    const cfg = LESSON_TYPE_CONFIG[type] || { bg: C.gray100, color: C.gray500, icon: FileText };
    const Icon = cfg.icon;
    return (
        <div style={{
            width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: cfg.bg, flexShrink: 0,
        }}>
            <Icon size={14} color={cfg.color} />
        </div>
    );
};

const LessonTypeBadge = ({ type }) => {
    const cfg = LESSON_TYPE_CONFIG[type] || { label: capitalizeFirst(type || ''), bg: C.gray100, color: C.gray600 };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '2px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700,
            background: cfg.bg, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
            {cfg.label}
        </span>
    );
};

const LessonCard = ({ lesson, lessonIndex, onPlayVideo }) => {
    const videoFile = lesson.files?.find(f => f.type === 'video' && f.url);
    const ytFile = lesson.files?.find(f => f.type === 'youtube' && f.url);
    const docFile = lesson.files?.find(f => (f.type === 'file' || f.type === 'document') && f.url);

    return (
        <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '12px 14px', background: '#fff',
            borderRadius: 9, border: `1px solid ${C.gray200}`,
            transition: 'box-shadow 0.15s, border-color 0.15s',
        }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)'; e.currentTarget.style.borderColor = C.green; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = C.gray200; }}
        >
            {/* Number */}
            <div style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: C.green100, color: C.greenMid, fontSize: 11, fontWeight: 700,
            }}>
                {lessonIndex + 1}
            </div>

            <LessonTypeIcon type={lesson.type} />

            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5, marginBottom: 3 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: C.gray800, margin: 0, textTransform: 'capitalize', wordBreak: 'break-word' }}>
                        {lesson.title || 'Untitled Lesson'}
                    </p>
                    {lesson.isGraded && (
                        <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: C.yellowLight, color: C.yellowDark }}>
                            Graded
                        </span>
                    )}
                </div>

                {lesson.description && (
                    <p style={{ fontSize: 12, color: C.gray500, margin: '0 0 5px', textTransform: 'capitalize', wordBreak: 'break-word', lineHeight: 1.5 }}>
                        {lesson.description}
                    </p>
                )}

                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <LessonTypeBadge type={lesson.type} />

                    {lesson.duration > 0 && (lesson.type === 'video' || lesson.type === 'youtube') && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: C.gray400 }}>
                            <Clock size={10} /> {lesson.duration} min
                        </span>
                    )}

                    {/* Play – uploaded video */}
                    {lesson.type === 'video' && videoFile && (
                        <button
                            onClick={() => onPlayVideo({ title: lesson.title, type: 'video', url: videoFile.url })}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                                background: C.blue, color: '#fff', border: 'none', cursor: 'pointer',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = C.blueDark}
                            onMouseLeave={e => e.currentTarget.style.background = C.blue}
                        >
                            <Play size={10} fill="currentColor" /> Play
                        </button>
                    )}

                    {/* YouTube */}
                    {lesson.type === 'youtube' && ytFile && (
                        <a
                            href={ytFile.url} target="_blank" rel="noopener noreferrer"
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                                background: '#dc2626', color: '#fff', textDecoration: 'none',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#b91c1c'}
                            onMouseLeave={e => e.currentTarget.style.background = '#dc2626'}
                        >
                            <Play size={10} fill="currentColor" /> Watch
                        </a>
                    )}

                    {/* Document */}
                    {(lesson.type === 'file' || lesson.type === 'document') && docFile && (
                        <button
                            onClick={() => handleDownloadUploadedFile(docFile.url, docFile.name || lesson.title)}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                                background: C.purple, color: '#fff', border: 'none', cursor: 'pointer',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#6d28d9'}
                            onMouseLeave={e => e.currentTarget.style.background = C.purple}
                        >
                            <Download size={10} /> Download Doc
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const ModuleAccordion = ({ module, moduleIndex, onPlayVideo }) => {
    const [expanded, setExpanded] = useState(moduleIndex === 0);
    const lessonCount = module.lessons?.length || 0;
    const totalDuration = (module.lessons || []).reduce((acc, l) => acc + (l.duration || 0), 0);

    return (
        <div style={{
            border: `1px solid ${expanded ? C.green : C.gray200}`,
            borderRadius: 12, marginBottom: 12, overflow: 'hidden',
            boxShadow: expanded ? `0 4px 14px rgba(34,197,94,0.10)` : '0 1px 3px rgba(0,0,0,0.04)',
            transition: 'box-shadow 0.2s, border-color 0.2s',
        }}>
            {/* Module header */}
            <button
                onClick={() => setExpanded(v => !v)}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '15px 18px',
                    background: expanded
                        ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                        : C.gray50,
                    border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {/* Module number badge */}
                    <div style={{
                        width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: expanded ? C.green : C.green100,
                        color: expanded ? '#fff' : C.greenMid,
                        fontSize: 13, fontWeight: 700, flexShrink: 0, transition: 'all 0.2s',
                    }}>
                        {moduleIndex + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{
                            fontSize: 13, fontWeight: 700, color: C.gray800, margin: 0,
                            textTransform: 'capitalize', wordBreak: 'break-word',
                        }}>
                            {module.title || 'Untitled Module'}
                        </h4>
                        {module.description && (
                            <p style={{ fontSize: 12, color: C.gray500, margin: '2px 0 0', textTransform: 'capitalize' }}>
                                {module.description}
                            </p>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, marginLeft: 12 }}>
                    {lessonCount > 0 && (
                        <div style={{ textAlign: 'right' }}>
                            <p style={{ fontSize: 12, fontWeight: 600, color: C.greenMid, margin: 0 }}>
                                {lessonCount} {lessonCount === 1 ? 'lesson' : 'lessons'}
                            </p>
                            {totalDuration > 0 && (
                                <p style={{ fontSize: 11, color: C.gray400, margin: 0 }}>{formatDuration(totalDuration)}</p>
                            )}
                        </div>
                    )}
                    <div style={{
                        width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: expanded ? C.green100 : C.gray200,
                        transition: 'background 0.2s',
                    }}>
                        {expanded
                            ? <ChevronUp size={14} color={C.greenMid} />
                            : <ChevronDown size={14} color={C.gray600} />}
                    </div>
                </div>
            </button>

            {/* Lesson list */}
            {expanded && (
                <div style={{ padding: '12px 18px 16px', background: C.gray50 }}>
                    {lessonCount > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <p style={{
                                fontSize: 10, fontWeight: 700, color: C.gray400,
                                textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4,
                            }}>
                                Lessons
                            </p>
                            {module.lessons.map((lesson, idx) => (
                                <LessonCard
                                    key={lesson.id || lesson.lessonId || idx}
                                    lesson={lesson}
                                    lessonIndex={idx}
                                    onPlayVideo={onPlayVideo}
                                />
                            ))}
                        </div>
                    ) : (
                        <p style={{ fontSize: 13, color: C.gray400, fontStyle: 'italic', textAlign: 'center', padding: '12px 0' }}>
                            No lessons added yet
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

// Video Modal
const VideoModal = ({ lesson, onClose }) => {
    useEffect(() => {
        const h = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', h);
        return () => window.removeEventListener('keydown', h);
    }, [onClose]);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(4px)',
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    width: '90vw', maxWidth: 860,
                    background: '#0f172a', borderRadius: 16, overflow: 'hidden',
                    boxShadow: '0 40px 100px rgba(0,0,0,0.6)',
                }}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', background: '#020617', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 7, background: lesson.type === 'youtube' ? '#dc262620' : `${C.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Video size={14} color={lesson.type === 'youtube' ? '#ef4444' : C.blue} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 520 }}>
                            {lesson.title}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'rgba(255,255,255,0.07)', border: 'none', cursor: 'pointer',
                            transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.14)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                    >
                        <X size={14} color="#94a3b8" />
                    </button>
                </div>

                {lesson.type === 'video' && (
                    <video controls autoPlay src={lesson.url}
                        style={{ width: '100%', maxHeight: '72vh', background: '#000', display: 'block' }}>
                        Your browser does not support the video tag.
                    </video>
                )}

                {lesson.type === 'youtube' && lesson.videoId && (
                    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                        <iframe
                            title={lesson.title}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                            src={`https://www.youtube.com/embed/${lesson.videoId}?autoplay=1`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

/**
 * CourseDetailPage
 *
 * Props:
 *   courseId (string)       – The course ID. Falls back to route param :id.
 *   courseData (object)     – Pre-loaded course (skips fetch).
 *   modules (array)         – Pre-loaded modules (skips fetch).
 *   onBack (func)           – Back button callback. Defaults to navigate(-1).
 */
const CourseDetailPage = ({ courseId: propCourseId, courseData: propCourseData, modules: propModules, onBack }) => {
    const params = useParams();
    const navigate = useNavigate();

    const courseId = propCourseId || params.id;
    const handleBack = onBack || (() => navigate(-1));

    const [course, setCourse] = useState(propCourseData || null);
    const [modules, setModules] = useState(propModules || []);
    const [loading, setLoading] = useState(!propCourseData);
    const [error, setError] = useState(null);
    const [playingLesson, setPlayingLesson] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    // Aggregate stats
    const stats = React.useMemo(() => {
        const totalLessons = modules.reduce((a, m) => a + (m.lessons?.length || 0), 0);
        const totalDuration = modules.reduce((a, m) => a + (m.lessons || []).reduce((b, l) => b + (l.duration || 0), 0), 0);
        const videoCount = modules.reduce((a, m) => a + (m.lessons || []).filter(l => l.type === 'video' || l.type === 'youtube').length, 0);
        const docCount = modules.reduce((a, m) => a + (m.lessons || []).filter(l => l.type === 'file' || l.type === 'document').length, 0);
        return { totalLessons, totalDuration, videoCount, docCount };
    }, [modules]);

    // Fetch when no pre-loaded data
    useEffect(() => {
        if (propCourseData) return;
        if (!courseId) { setError('No course ID provided'); setLoading(false); return; }

        const fetchCourse = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await trainingService.getCourseStructure(courseId);
                if (res.success && res.data) {
                    if (res.data.course) setCourse(res.data.course);
                    setModules(res.data.modules || []);
                } else {
                    setError(res.error || 'Failed to load course details');
                }
            } catch {
                setError('Failed to load course details. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchCourse();
    }, [courseId, propCourseData]);

    useEffect(() => { if (propModules) setModules(propModules); }, [propModules]);

    const handlePlayVideo = useCallback((payload) => setPlayingLesson(payload), []);

    const handleExport = async () => {
        if (isExporting || !courseId) return;
        setIsExporting(true);
        const toastId = toast.loading('Preparing Excel export...');
        try {
            const exportName = course?.code || course?.courseCode || course?.courseId || course?.title || 'Course';
            const result = await trainingService.exportCourseDetail(courseId, exportName);
            if (result.success) {
                toast.success('Course exported successfully', { id: toastId });
            } else {
                toast.error(result.error || 'Failed to export course', { id: toastId });
            }
        } catch (err) {
            toast.error(err.message || 'Failed to export course', { id: toastId });
        } finally {
            setIsExporting(false);
        }
    };

    // Derived fields
    const diffCfg = DIFFICULTY_CONFIG[course?.difficulty || course?.level] || DIFFICULTY_CONFIG.beginner;
    const statusCfg = STATUS_CONFIG[course?.status] || STATUS_CONFIG.draft;
    const courseType = typeof course?.courseType === 'object' ? course.courseType?.name : course?.type || '';
    const objectives = normalizeObjectives(
        course?.learningObjectives || course?.objectives || course?.learningOutcomes || []
    );
    const prerequisites = course?.prerequisites;
    const instructor = typeof course?.instructor === 'object' ? course.instructor?.name : course?.instructor;
    const tags = course?.skills || course?.tags || [];

    // ── Loading ──────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
                <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    border: `3px solid ${C.green100}`, borderTop: `3px solid ${C.green}`,
                    animation: 'spin 0.8s linear infinite',
                }} />
                <p style={{ fontSize: 13, color: C.gray500, fontWeight: 500 }}>Loading course details…</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // ── Error ────────────────────────────────────────────────────────────────
    if (error) {
        return (
            <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 40 }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: C.redLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AlertCircle size={24} color={C.red} />
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: C.gray800, margin: 0 }}>Failed to Load Course</h3>
                <p style={{ fontSize: 13, color: C.gray500, textAlign: 'center', maxWidth: 340, margin: 0 }}>{error}</p>
                <button
                    onClick={handleBack}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 8,
                        padding: '9px 18px', borderRadius: 9, border: 'none', cursor: 'pointer',
                        background: C.green, color: '#fff', fontSize: 13, fontWeight: 600,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = C.greenMid}
                    onMouseLeave={e => e.currentTarget.style.background = C.green}
                >
                    <ArrowLeft size={14} /> Go Back
                </button>
            </div>
        );
    }

    // ── Main Render ──────────────────────────────────────────────────────────
    return (
        <div style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif", minHeight: '100vh', background: C.gray50 }}>

            {/* ── Hero Header ──────────────────────────────────────────────── */}
            <div style={{ background: C.heroBg, padding: '0 0 28px', position: 'relative', overflow: 'hidden' }}>
                {/* decorative rings */}
                <div style={{ position: 'absolute', width: 380, height: 380, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', top: -140, right: -80, pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', bottom: -80, left: 60, pointerEvents: 'none' }} />

                <div style={{ margin: '0 auto', padding: '0 24px' }}>
                    {/* Back btn */}
                    <div style={{ paddingTop: 22, paddingBottom: 6 }} className='flex justify-between items-start gap-3 flex-wrap'>
                        <button
                            onClick={handleBack}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 7,
                                padding: '6px 14px', borderRadius: 7,
                                background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
                                color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer',
                                transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                        >
                            <ArrowLeft size={13} /> Back to Courses
                        </button>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, margin: '0' }}>
                            <button
                                onClick={handleExport}
                                disabled={isExporting}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 7,
                                    padding: '6px 14px', borderRadius: 7,
                                    background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
                                    color: '#fff', fontSize: 13, fontWeight: 500,
                                    cursor: isExporting ? 'not-allowed' : 'pointer',
                                    opacity: isExporting ? 0.7 : 1,
                                    transition: 'background 0.15s',
                                }}
                                onMouseEnter={e => { if (!isExporting) e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                            >
                                <Download size={13} /> {isExporting ? 'Exporting...' : 'Export'}
                            </button>
                            {courseType && (
                                <span style={{
                                    padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                                    background: 'rgba(255,255,255,0.15)', color: '#fff',
                                    border: '1px solid rgba(255,255,255,0.25)',
                                }}>
                                    {capitalizeValue(courseType)}
                                </span>
                            )}
                            <span style={{
                                padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                background: statusCfg.bg, color: statusCfg.color,
                                border: `1px solid ${statusCfg.color}40`,
                            }}>
                                {statusCfg.label}
                            </span>
                            {course?.isMandatory && (
                                <span style={{
                                    padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                    background: 'rgba(251,191,36,0.18)', color: '#fbbf24',
                                    border: '1px solid rgba(251,191,36,0.3)',
                                }}>
                                    ⚑ Mandatory
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Badges row */}


                    <h1 style={{
                        fontSize: 'clamp(19px, 3vw, 30px)', fontWeight: 800, color: '#fff',
                        margin: '0 0 8px', lineHeight: 1.25, textTransform: 'capitalize', wordBreak: 'break-word',
                    }}>
                        {course?.title || 'Untitled Course'}
                    </h1>

                    {course?.code && (
                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', fontFamily: 'monospace', marginBottom: 6 }}>
                            Code: <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>{course.code}</span>
                        </p>
                    )}

                    {/* Instructor */}
                    {instructor && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                            <div style={{
                                width: 30, height: 30, borderRadius: '50%',
                                background: 'linear-gradient(135deg,#bbf7d0,#22c55e)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 13, fontWeight: 700, color: C.greenDark,
                            }}>
                                {instructor.charAt(0).toUpperCase()}
                            </div>
                            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                                {instructor}
                            </span>
                        </div>
                    )}

                    {/* Info badges grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, maxWidth: 720 }}>
                        <InfoBadge icon={Layers} label="Modules" value={`${modules.length} module${modules.length !== 1 ? 's' : ''}`} />
                        <InfoBadge icon={BookOpen} label="Lessons" value={`${stats.totalLessons} lesson${stats.totalLessons !== 1 ? 's' : ''}`} />
                        {course?.duration > 0 && (
                            <InfoBadge icon={Clock} label="Duration" value={formatDuration(course.duration)} />
                        )}
                        <InfoBadge icon={BarChart2} label="Difficulty" value={`${diffCfg.icon} ${diffCfg.label}`} />
                        {course?.enrolledCount > 0 && (
                            <InfoBadge icon={Users} label="Enrolled" value={`${course.enrolledCount} learner${course.enrolledCount !== 1 ? 's' : ''}`} />
                        )}
                    </div>
                </div>
            </div>

            {/* ── Body ─────────────────────────────────────────────────────── */}
            <div style={{ margin: '0 auto', padding: '28px 24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>

                    {/* Left – main content */}
                    <div>
                        {/* About */}
                        {(course?.description || course?.summary) && (
                            <SectionCard title="About This Course" icon={Info} accent={C.blue}>
                                <p style={{ fontSize: 13, color: C.gray700, lineHeight: 1.8, margin: 0, textTransform: 'capitalize', wordBreak: 'break-word' }}>
                                    {course.description || course.summary}
                                </p>
                            </SectionCard>
                        )}

                        {/* Learning Objectives */}
                        {objectives.length > 0 && objectives.some(o => (o || '').trim()) && (
                            <SectionCard title="Learning Objectives" icon={Target} accent={C.green}>
                                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {objectives.filter(o => (o || '').trim()).map((obj, i) => (
                                        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                            <div style={{
                                                width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                                                background: C.green100, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <CheckCircle size={12} color={C.greenMid} />
                                            </div>
                                            <span style={{ fontSize: 13, color: C.gray700, textTransform: 'capitalize', wordBreak: 'break-word', lineHeight: 1.6 }}>
                                                {obj}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </SectionCard>
                        )}

                        {/* Prerequisites */}
                        {prerequisites && (
                            <SectionCard title="Prerequisites" icon={AlertCircle} accent={C.yellow}>
                                <p style={{ fontSize: 13, color: C.gray700, lineHeight: 1.7, margin: 0, textTransform: 'capitalize', wordBreak: 'break-word' }}>
                                    {typeof prerequisites === 'string'
                                        ? prerequisites
                                        : Array.isArray(prerequisites)
                                            ? prerequisites.join(', ')
                                            : String(prerequisites)}
                                </p>
                            </SectionCard>
                        )}

                        {/* Course Structure */}
                        <SectionCard title="Course Structure" icon={BookOpen} accent={C.green}>
                            {modules.length > 0 ? (
                                <>
                                    {/* Summary bar */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18,
                                        padding: '11px 14px', background: C.green50, borderRadius: 9,
                                        border: `1px solid ${C.green100}`,
                                    }}>
                                        <Layers size={15} color={C.greenMid} />
                                        <div style={{ flex: 1 }}>
                                            <p style={{ fontSize: 13, fontWeight: 600, color: C.greenMid, margin: '0 0 3px' }}>
                                                {modules.length} Module{modules.length !== 1 ? 's' : ''} · {stats.totalLessons} Lesson{stats.totalLessons !== 1 ? 's' : ''}
                                            </p>
                                            <div style={{ display: 'flex', gap: 12 }}>
                                                {stats.videoCount > 0 && (
                                                    <span style={{ fontSize: 11, color: C.gray500 }}>📹 {stats.videoCount} video{stats.videoCount !== 1 ? 's' : ''}</span>
                                                )}
                                                {stats.docCount > 0 && (
                                                    <span style={{ fontSize: 11, color: C.gray500 }}>📄 {stats.docCount} doc{stats.docCount !== 1 ? 's' : ''}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {modules.map((module, idx) => (
                                        <ModuleAccordion
                                            key={module.id || module._id || idx}
                                            module={module}
                                            moduleIndex={idx}
                                            onPlayVideo={handlePlayVideo}
                                        />
                                    ))}
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '28px 0', color: C.gray400 }}>
                                    <BookOpen size={32} style={{ marginBottom: 10, opacity: 0.35 }} />
                                    <p style={{ fontSize: 13, margin: 0 }}>No modules have been added to this course yet.</p>
                                </div>
                            )}
                        </SectionCard>
                    </div>

                    {/* Right – sidebar */}
                    <div style={{ position: 'sticky', top: 24 }}>
                        {/* Course Info card */}
                        <div style={{
                            background: '#fff', borderRadius: 12, border: `1px solid ${C.gray200}`,
                            boxShadow: '0 2px 12px rgba(0,0,0,0.05)', overflow: 'hidden', marginBottom: 18,
                        }}>
                            <div style={{
                                padding: '14px 18px', borderBottom: `1px solid ${C.gray100}`,
                                background: C.gray50, display: 'flex', alignItems: 'center', gap: 8,
                            }}>
                                <Info size={14} color={C.blue} />
                                <h3 style={{ fontSize: 13, fontWeight: 700, color: C.gray800, margin: 0 }}>Course Info</h3>
                            </div>
                            <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {[
                                    { label: 'Course Code', value: course?.code || course?.courseId, mono: true },
                                    { label: 'Type', value: courseType ? capitalizeValue(courseType) : null },
                                    { label: 'Category', value: course?.category ? capitalizeValue(course.category) : null },
                                    { label: 'Difficulty', value: diffCfg ? `${diffCfg.icon} ${diffCfg.label}` : null },
                                    { label: 'Status', value: statusCfg?.label },
                                    { label: 'Duration', value: course?.duration ? formatDuration(course.duration) : null },
                                    { label: 'Instructor', value: instructor },
                                    { label: 'Mandatory', value: course?.isMandatory !== undefined ? (course.isMandatory ? 'Yes' : 'No') : null },
                                    { label: 'Max Attempts', value: course?.assessment?.maxAttempts || course?.maxAttempts },
                                    { label: 'Certificate', value: course?.certificate?.enabled ? 'Awarded on Pass' : null },
                                    {
                                        label: 'Created',
                                        value: course?.createdAt
                                            ? new Date(course.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                            : null,
                                    },
                                ].filter(item => item.value !== null && item.value !== undefined && item.value !== '').map(({ label, value, mono }) => (
                                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                        <span style={{ fontSize: 12, color: C.gray500, fontWeight: 500, flexShrink: 0 }}>{label}</span>
                                        <span style={{
                                            fontSize: 12, color: C.gray800, fontWeight: 600,
                                            textAlign: 'right', wordBreak: 'break-word',
                                            fontFamily: mono ? 'monospace' : 'inherit',
                                            background: mono ? C.gray100 : 'transparent',
                                            padding: mono ? '2px 6px' : 0,
                                            borderRadius: mono ? 4 : 0,
                                        }}>
                                            {String(value)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Tags */}
                        {tags.length > 0 && (
                            <div style={{
                                background: '#fff', borderRadius: 12, border: `1px solid ${C.gray200}`,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '14px 18px', marginBottom: 18,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                    <Tag size={13} color={C.blue} />
                                    <h3 style={{ fontSize: 13, fontWeight: 700, color: C.gray800, margin: 0 }}>Skills & Tags</h3>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {tags.map((tag, i) => (
                                        <span key={i} style={{
                                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                                            background: C.blue50, color: C.blue, border: `1px solid ${C.blue100}`,
                                        }}>
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Content summary card */}
                        <div style={{
                            background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)`,
                            borderRadius: 12, padding: '18px', marginBottom: 18,
                            boxShadow: `0 8px 20px rgba(34,197,94,0.28)`,
                        }}>
                            <h3 style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.8)', margin: '0 0 14px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                Content Summary
                            </h3>
                            {[
                                { label: 'Modules', value: modules.length, icon: Layers },
                                { label: 'Lessons', value: stats.totalLessons, icon: BookOpen },
                                { label: 'Video Lessons', value: stats.videoCount, icon: Video },
                                { label: 'Documents', value: stats.docCount, icon: FileText },
                            ].map(({ label, value, icon: Icon }) => (
                                <div key={label} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.12)',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Icon size={13} color="rgba(255,255,255,0.75)" />
                                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.82)' }}>{label}</span>
                                    </div>
                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Video Modal */}
            {playingLesson && (
                <VideoModal lesson={playingLesson} onClose={() => setPlayingLesson(null)} />
            )}
        </div>
    );
};

export default CourseDetailPage;
